import React, { useState } from 'react';
import { Input } from '../UI/V3/Forms/Input/Input';
import Button from '../UI/V3/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import styles from './SecurityContent.module.scss';

export const SecurityContent: React.FC = () => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your new password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            // TODO: Implement password change functionality
            console.log('Changing password...');
            // Reset form on success
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        }
    };

    const handleCancel = () => {
        setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setErrors({});
    };

    return (
        <div className={styles.securityContent}>
            <div className={styles.header}>
                <h2 className={styles.title}>Security</h2>
            </div>

            <div className={styles.content}>
                <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
                    <div className={styles.formField}>
                        <div className={styles.passwordInputContainer}>
                            <Input
                                id="currentPassword"
                                label="Current password"
                                type={showPasswords.current ? 'text' : 'password'}
                                value={formData.currentPassword}
                                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                                error={errors.currentPassword}
                                placeholder="Enter your current password"
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => togglePasswordVisibility('current')}
                                tabIndex={-1}
                            >
                                <FontAwesomeIcon 
                                    icon={showPasswords.current ? faEyeSlash : faEye}
                                    size="sm"
                                />
                            </button>
                        </div>
                    </div>

                    <div className={styles.formField}>
                        <div className={styles.passwordInputContainer}>
                            <Input
                                id="newPassword"
                                label="New password"
                                type={showPasswords.new ? 'text' : 'password'}
                                value={formData.newPassword}
                                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                                error={errors.newPassword}
                                placeholder="Enter your new password"
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => togglePasswordVisibility('new')}
                                tabIndex={-1}
                            >
                                <FontAwesomeIcon 
                                    icon={showPasswords.new ? faEyeSlash : faEye}
                                    size="sm"
                                />
                            </button>
                        </div>
                    </div>
                    

                    <div className={styles.formField}>
                        <div className={styles.passwordInputContainer}>
                            <Input
                                id="confirmPassword"
                                label="Confirm new password"
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                error={errors.confirmPassword}
                                placeholder="Confirm your new password"
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => togglePasswordVisibility('confirm')}
                                tabIndex={-1}
                            >
                                <FontAwesomeIcon 
                                    icon={showPasswords.confirm ? faEyeSlash : faEye}
                                    size="sm"
                                />
                            </button>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.actions}>
                        <Button
                            label="Update password"
                            variant="black"
                            onClick={handleSave}
                            size="large"
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SecurityContent;
